import {
    Content,
    Group,
    Icon,
    Item,
    ItemIndicator,
    ItemText,
    Portal,
    Root,
    Separator,
    Trigger,
    Value,
    Viewport,
} from '@radix-ui/react-select';
import clsx from 'clsx';

export { Content, Group, Icon, Item, ItemIndicator, ItemText, Portal, Root, Separator, Trigger, Value, Viewport };

import type { ComponentProps, FunctionComponent, ReactNode } from 'react';

import { useColorSchemeScope } from '../color-scheme/ColorSchemeScope';
import { IconChevronGrabberVertical } from '../icons';
import { CheckIcon } from '../icons/CheckIcon';
import { floatingSurfaceClassNames, floatingSurfaceItemClassNames } from '../surface/surfaceClasses';

export type SelectProps = {
    readonly 'aria-label'?: string;
    readonly children: ReactNode;
    readonly defaultValue?: string;
    readonly disabled?: boolean;
    readonly onValueChange?: (value: string) => void;
    readonly placeholder?: string;
    readonly portalContainer?: HTMLElement | null;
    readonly value?: string;
    readonly variant?: 'default' | 'error' | 'success';
};

export type SelectItemProps = {
    readonly children: ReactNode;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly value: string;
};

export type SelectGroupProps = {
    readonly children: ReactNode;
    readonly label?: string;
};

export type SelectSeparatorProps = {
    readonly className?: string;
};

const variantBorderClasses = {
    default: 'border-border-subtle',
    error: 'border-border-error',
    success: 'border-border-success',
} as const;

const variantFocusClasses = {
    default: 'focus:border-border-focus',
    error: 'focus:border-border-error-strong',
    success: 'focus:border-border-success-strong',
} as const;

export const StyledContent: FunctionComponent<ComponentProps<typeof Content>> = ({ className, ...props }) => {
    const colorScheme = useColorSchemeScope();

    return (
        <Content
            className={clsx(floatingSurfaceClassNames.tone.white, 'rounded-md overflow-hidden z-[1205]', className)}
            data-color-scheme={colorScheme}
            {...props}
        />
    );
};

export const StyledViewport: FunctionComponent<ComponentProps<typeof Viewport>> = ({ className, ...props }) => (
    <Viewport className={clsx('bg-surface-raised p-xs', className)} {...props} />
);

export const StyledItem: FunctionComponent<ComponentProps<typeof Item> & SelectItemProps> = ({
    className,
    ...props
}) => (
    <Item
        className={clsx(
            'flex flex-row items-start gap-s rounded-sm relative select-none',
            'text-meta font-normal text-text-secondary',
            'py-s px-m cursor-pointer',
            floatingSurfaceItemClassNames.hover,
            'data-[disabled]:text-text-quiet data-[disabled]:cursor-default data-[disabled]:pointer-events-none',
            floatingSurfaceItemClassNames.selectHighlighted,
            className,
        )}
        {...props}
    />
);

export const Select: FunctionComponent<SelectProps> = ({
    'aria-label': ariaLabel,
    children,
    defaultValue,
    disabled = false,
    onValueChange,
    placeholder,
    portalContainer,
    value,
    variant = 'default',
    ...props
}) => {
    return (
        <Root defaultValue={defaultValue} disabled={disabled} value={value} onValueChange={onValueChange} {...props}>
            <Trigger
                aria-label={ariaLabel}
                className={clsx(
                    'flex items-center justify-between w-full',
                    'bg-surface-raised border rounded-md outline-none',
                    'text-meta font-sans font-normal text-text-secondary tracking-tight',
                    'py-m px-l',
                    'transition-all duration-100 ease-in-out',
                    'hover:bg-surface-subtle',
                    'data-[disabled]:text-text-quiet data-[disabled]:cursor-default data-[disabled]:pointer-events-none',
                    'data-[placeholder]:text-text-placeholder',
                    variantBorderClasses[variant],
                    disabled ? 'cursor-default opacity-50' : clsx('cursor-pointer', variantFocusClasses[variant]),
                )}
            >
                <Value placeholder={placeholder} />
                {/* h-4 pins the icon to the text line box; a taller glyph would make the trigger
                    stand higher than an Input with the same padding and type scale. */}
                <Icon className="flex h-4 shrink-0 items-center text-text-placeholder transition-transform duration-200 ease-in-out [&>svg]:h-[20px] [&>svg]:w-[20px]">
                    <IconChevronGrabberVertical />
                </Icon>
            </Trigger>
            <Portal container={portalContainer ?? undefined}>
                <StyledContent>
                    <StyledViewport>{children}</StyledViewport>
                </StyledContent>
            </Portal>
        </Root>
    );
};

export const SelectItem: FunctionComponent<SelectItemProps> = ({ children, disabled, value, ...props }) => {
    return (
        <StyledItem disabled={disabled} value={value} {...props}>
            <ItemText className="block flex-1">{children}</ItemText>
            <ItemIndicator className="inline-flex items-center justify-center text-text-secondary ml-s">
                <CheckIcon />
            </ItemIndicator>
        </StyledItem>
    );
};

export const SelectGroup: FunctionComponent<SelectGroupProps> = ({ children, label, ...props }) => {
    return (
        <Group
            className="data-[disabled]:text-text-quiet data-[disabled]:cursor-default data-[disabled]:pointer-events-none"
            {...props}
        >
            {label !== undefined && <div className="text-text-placeholder text-meta font-normal p-s">{label}</div>}
            {children}
        </Group>
    );
};

export const SelectSeparator: FunctionComponent<SelectSeparatorProps> = ({ className, ...props }) => {
    return <Separator className={clsx('bg-border-subtle h-px m-xs', className)} {...props} />;
};
